import type { QueryClient } from '@tanstack/query-core';
import { getAccountsForUserQuery } from 'src/hooks/accounts/useAccounts';

export const findAccountIdByAccountName = async (
    queryClient: QueryClient | undefined,
    accountName: string | null,
): Promise<string | null> => {
    if (!queryClient || !accountName) return null;
    const accounts = await queryClient.fetchQuery(getAccountsForUserQuery('reader'));
    return accounts.find((account) => account.name === accountName)?.id ?? null;
};

export function escapeODataValue(value: string | undefined): string | undefined {
    return value ? value.replace(/'/g, "''") : undefined;
}

export const generateOdataFilterFromOrderParams = async (params: URLSearchParams): Promise<string> => {
    let $filter: string = '';
    const filterParts = [];
    const queryClient = window.reactContainer?.get('QueryClient');
    if (params.get('startDate') && params.get('endDate')) {
        const lookBackDate = params.get('startDate');
        const untilDate = params.get('endDate');
        filterParts.push(`createdAt gt '${lookBackDate}' and createdAt lt '${untilDate}'`);
        params.delete('startDate');
        params.delete('endDate');
    }

    // Add firm filter
    if (params.get('firm')) {
        const firm = params.get('firm');
        filterParts.push(`firmId eq '${escapeODataValue(firm ?? undefined)}'`);
        params.delete('firm');
    }

    // Add symbol filter
    if (params.get('symbol')) {
        const symbol = params.get('symbol');
        filterParts.push(`symbol eq '${escapeODataValue(symbol?.toUpperCase())}'`);
        params.delete('symbol');
    }
    // Add quantity filter
    if (params.get('quantity')) {
        const quantity = params.get('quantity');
        filterParts.push(`quantity eq ${escapeODataValue(quantity ?? undefined)}`);
        params.delete('quantity');
    }

    if (params.get('marketParticipantId')) {
        const marketParticipantId = params.get('marketParticipantId');
        filterParts.push(`marketParticipantId eq '${escapeODataValue(marketParticipantId?.toUpperCase())}'`);
        params.delete('marketParticipantId');
    }

    // Add route filter
    if (params.get('route')) {
        const route = params.get('route');
        filterParts.push(`route eq '${escapeODataValue(route?.toUpperCase())}'`);
        params.delete('route');
    }

    // Add state filter
    if (params.get('state')) {
        const state = params.get('state');
        filterParts.push(`state eq '${escapeODataValue(state ?? undefined)}'`);
        params.delete('state');
    }

    // Add user filter
    if (params.get('user')) {
        const user = params.get('user');
        filterParts.push(`user eq '${escapeODataValue(user ?? undefined)}'`);
        params.delete('user');
    }

    // Add account name filter - account name is found in the createdBy object - username
    if (params.get('account')) {
        const account = params.get('account');
        const matchedAccountId = await findAccountIdByAccountName(queryClient, account);
        if (matchedAccountId) {
            filterParts.push(`account eq '${matchedAccountId}'`);
        }
        params.delete('account');
    }

    // Combine all filters with 'and' operator
    if (filterParts.length > 0) {
        $filter = filterParts.map((part) => `(${part})`).join(' and ');
    }

    return $filter;
};
